'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { taxAdjustedBalance } from '@/lib/tax';

export interface SeatRef {
  poolId: string;
  seatIndex: number;
  managerAddress: string;
}

interface BalancesResponse {
  balances: Record<string, string>;
}

async function fetchLiveBalances(seats: SeatRef[]): Promise<Map<string, bigint>> {
  if (seats.length === 0) return new Map();

  const res = await fetch('/api/rpc/seat-balances', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ seats }),
  });

  if (!res.ok) return new Map();

  const data: BalancesResponse = await res.json();
  const map = new Map<string, bigint>();
  for (const [key, value] of Object.entries(data.balances)) {
    map.set(key, BigInt(value));
  }
  return map;
}

/**
 * Fetches live onchain currentBalance for a set of seats.
 *
 * Polls every 30s and refetches on window focus. Returns the multicall
 * sampledAt (`dataUpdatedAt`, ms since epoch) so callers that know the
 * seat's `price` + `taxRateBps` can apply continuous client-side
 * Harberger tax decay between polls via `decayedLiveBalance` — that
 * pairing is the only way the manage drawer's "minutes left" display
 * ticks down per second instead of plateauing for ~30s at a time and
 * looking frozen mid-runway. (See `decayedLiveBalance` for the decay
 * helper; the drawer also pairs it with `useLiveTick(1000)` so React
 * re-renders the value every second.)
 */
export function useLiveBalances(
  seats: SeatRef[] | undefined,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const stableKey = useMemo(
    () =>
      seats
        ?.map((s) => `${s.poolId}:${s.seatIndex}`)
        .sort()
        .join(',') ?? '',
    [seats],
  );

  const enabled = (options?.enabled ?? true) && !!seats && seats.length > 0;

  const { data, isLoading, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['live-balances', stableKey],
    queryFn: () => fetchLiveBalances(seats!),
    enabled,
    staleTime: 10_000,
    refetchInterval: options?.refetchInterval ?? 30_000,
    refetchOnWindowFocus: true,
  });

  return {
    balances: data ?? new Map<string, bigint>(),
    isLoading,
    refetch,
    /** Wall-clock ms when the most recent multicall returned. Use as
     *  the anchor for `decayedLiveBalance`. */
    sampledAt: dataUpdatedAt,
  };
}

/** Look up a single seat's live balance from the map. */
export function getLiveBalance(
  map: Map<string, bigint>,
  poolId: string,
  seatIndex: number,
): bigint | undefined {
  return map.get(`${poolId}:${seatIndex}`);
}

/**
 * Apply continuous Harberger tax decay to a `useLiveBalances` snapshot.
 *
 * The onchain `currentBalance(gridId, seatId)` decays every block by
 * `price × taxRateBps × elapsed / (ONE_WEEK × BPS)` (UnifiedGridManagerV22
 * `currentBalance` view). `useLiveBalances` only re-samples that value
 * every 30 s by default, so any UI that reads the raw map directly
 * appears to plateau (and the runway derived from it appears frozen)
 * between polls. Re-applying the contract's tax formula client-side
 * against the wall-clock anchor (`sampledAt` from `useLiveBalances`)
 * gives a per-render value that matches what the next poll would
 * return — modulo block-time jitter, which is bounded to ~2s on Base.
 *
 * Returns the post-decay balance clamped to 0. When `sampledAt` is
 * 0/undefined (no successful poll yet) we return the raw value
 * unchanged so the caller's existing fallback chain still applies.
 */
export function decayedLiveBalance(params: {
  rawBalance: bigint | undefined;
  price: bigint;
  taxRateBps: bigint;
  sampledAt: number | undefined;
  /** ms since epoch — defaults to `Date.now()`. Threading this in
   *  keeps the decay deterministic for tests. */
  nowMs?: number;
}): bigint | undefined {
  const { rawBalance, price, taxRateBps, sampledAt, nowMs } = params;
  if (rawBalance === undefined) return undefined;
  if (!sampledAt || sampledAt <= 0) return rawBalance;
  const sampledAtSec = Math.floor(sampledAt / 1000);
  const nowSec = Math.floor((nowMs ?? Date.now()) / 1000);
  return taxAdjustedBalance(rawBalance, price, taxRateBps, sampledAtSec, nowSec);
}

/**
 * 1 Hz render tick for callers that drive a `decayedLiveBalance`
 * computation off `Date.now()`. Returns a value that increments every
 * second, suitable for embedding in a `useMemo`/effect dep so React
 * re-runs the decay between `useLiveBalances` polls.
 *
 * The interval suspends when `enabled === false` so closed drawers /
 * unmounted views don't burn re-renders.
 */
export function useLiveTick(intervalMs: number = 1000, enabled: boolean = true): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
  return tick;
}
